import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import auth from '../helper/auth';
import MasterLayout from '../masterLayout/MasterLayout';

const TosPage = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [content, setContent] = useState('');
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [canAccept, setCanAccept] = useState(false);
    const [accepting, setAccepting] = useState(false);
    const [showDeclineModal, setShowDeclineModal] = useState(false);
    
    const sentinelRef = useRef(null);
    const isGuardMode = searchParams.get('mode') === 'guard';

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [contentRes, statusRes] = await Promise.all([
                    fetch(`${config.API_BASE_URL}/api/tos/content`),
                    auth.fetch(`${config.API_BASE_URL}/api/tos/status`)
                ]);
                
                const contentData = await contentRes.json();
                const statusData = await statusRes.json();
                
                setContent(contentData.content);
                setStatus(statusData);
                
                if (!statusData.requiresAcceptance && isGuardMode) {
                    navigate('/applications', { replace: true });
                }
            } catch (err) {
                console.error('Failed to fetch ToS data:', err);
                toast.error('Failed to load Terms of Service');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [navigate, isGuardMode]);

    useEffect(() => {
        if (!content || !status?.requiresAcceptance) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setCanAccept(true);
                }
            },
            { threshold: 1.0 }
        );

        if (sentinelRef.current) {
            observer.observe(sentinelRef.current);
        }

        return () => observer.disconnect();
    }, [content, status]);

    const handleAccept = async () => {
        if (!canAccept || accepting) return;
        setAccepting(true);
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/tos/accept`, {
                method: 'POST',
                body: JSON.stringify({ version: status.version })
            });
            
            if (res.success) {
                toast.success('Terms of Service accepted');
                window.location.href = '/applications'; // Hard reload to clear guard state if any
            } else {
                throw new Error(res.error || 'Failed to accept ToS');
            }
        } catch (err) {
            console.error(err);
            toast.error(err.message);
        } finally {
            setAccepting(false);
        }
    };

    const handleDecline = () => {
        setShowDeclineModal(true);
    };

    const handleLogout = () => {
        logout();
        navigate('/sign-in');
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    const PageContent = (
        <div className="container-fluid py-4">
            <div className="row justify-content-center">
                <div className="col-12 col-xl-10">
                    <div className="card shadow-sm border-0 overflow-hidden">
                        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
                            <h5 className="mb-0 fw-bold">Terms of Service</h5>
                            {status && (
                                <span className={`badge ${status.requiresAcceptance ? 'bg-warning text-dark' : 'bg-success text-white'}`}>
                                    V{status.version} {status.accepted_at && `• Accepted ${new Date(status.accepted_at).toLocaleDateString()}`}
                                </span>
                            )}
                        </div>
                        <div className="card-body p-0 bg-light" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                            <div 
                                className="mx-auto my-5 p-4 p-md-5 bg-white shadow-sm border rounded" 
                                style={{ maxWidth: '750px', minHeight: '100%' }}
                            >
                                <div className="tos-content" dangerouslySetInnerHTML={{ __html: content }} />
                                {status?.requiresAcceptance && <div ref={sentinelRef} style={{ height: '1px' }} />}
                            </div>
                        </div>
                        {status?.requiresAcceptance && (
                            <div className="card-footer bg-white py-3 border-top sticky-bottom d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                                <div className="d-flex align-items-center text-muted small">
                                    <i className="ri-information-line me-2 text-primary" style={{ fontSize: '1.2rem' }}></i>
                                    <span>Please scroll to the bottom of the document to enable acceptance.</span>
                                </div>
                                <div className="d-flex gap-2 w-100 w-md-auto">
                                    <button 
                                        className="btn btn-outline-danger px-4" 
                                        onClick={handleDecline}
                                    >
                                        Decline
                                    </button>
                                    <button 
                                        className="btn btn-primary px-4" 
                                        disabled={!canAccept || accepting}
                                        onClick={handleAccept}
                                    >
                                        {accepting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Accepting...
                                            </>
                                        ) : 'I accept the Terms of Service \u2713'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Decline Modal */}
            {showDeclineModal && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header bg-danger text-white border-0">
                                <h5 className="modal-title">Action Required</h5>
                            </div>
                            <div className="modal-body py-4 text-center">
                                <i className="ri-error-warning-fill text-danger mb-3" style={{ fontSize: '3rem' }}></i>
                                <p className="mb-0">You must accept the Terms of Service to use the HubScroll platform. Declining will log you out of your account.</p>
                            </div>
                            <div className="modal-footer border-0 pt-0 justify-content-center">
                                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowDeclineModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-danger px-4" onClick={handleLogout}>Log Out</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    if (isGuardMode) {
        return (
            <div className="bg-light min-vh-100 py-5">
                {PageContent}
            </div>
        );
    }

    return (
        <MasterLayout>
            {PageContent}
        </MasterLayout>
    );
};

export default TosPage;
